import React, {useEffect} from 'react'
import {useTranslation} from '@wix/yoshi-flow-editor'
import {TimeSlot, Status} from '@wix/ambassador-table-reservations-v1-time-slot/types'
import {setHMToDate} from '@wix/table-reservations-lib/schedule'

import {TimeOption} from '../../utils/timeOptions'
import {LayoutSize} from '../LayoutSizeProvider/types'
import {NotAvailableTimeSlotNotification} from '../NotAvailableTimeSlotNotification'
import {SelectTimeSlotError} from '../SelectTimeSlotError'
import {TagsListProps} from '../TagsList'

import {timeSlotsListDataHooks} from './constants'

interface Props {
  regionalSettings?: string
  selectedTimeOption?: TimeOption
  items: TimeSlot[]
  selectedTimeSlot?: Date
  selectedDate?: Date
  partySize: number
  selectedAreaName?: string
  showNoAvailableTimeSlotNotification: boolean
  showSelectTimeSlotError: boolean
  onSelectTimeSlot: (item: TimeSlot) => void
  layoutSize: LayoutSize
  className?: string
  notificationClassName?: string
  renderTagsList: (props: TagsListProps) => React.ReactNode
}

export const TimeSlotsList: React.FC<Props> = ({
  layoutSize,
  regionalSettings,
  selectedTimeOption,
  items = [],
  selectedTimeSlot,
  selectedDate,
  partySize,
  selectedAreaName,
  showNoAvailableTimeSlotNotification,
  showSelectTimeSlotError,
  onSelectTimeSlot,
  renderTagsList,
  notificationClassName,
}) => {
  const {t} = useTranslation()

  useEffect(() => {
    if (selectedTimeSlot || showNoAvailableTimeSlotNotification) {
      return
    }

    const preferredTime = items.find(
      (item) =>
        (!selectedDate || item.startDate?.toDateString() === selectedDate.toDateString()) &&
        selectedTimeOption?.data.hours === item.startDate?.getHours() &&
        selectedTimeOption?.data.minutes === item.startDate?.getMinutes(),
    )

    if (preferredTime && preferredTime.status === Status.AVAILABLE) {
      onSelectTimeSlot(preferredTime)
    }
  }, [
    items,
    onSelectTimeSlot,
    selectedDate,
    selectedTimeOption,
    selectedTimeSlot,
    showNoAvailableTimeSlotNotification,
  ])

  if (!items?.length) {
    return null
  }

  return (
    <div data-hook={timeSlotsListDataHooks.root()}>
      {showSelectTimeSlotError && <SelectTimeSlotError />}
      {showNoAvailableTimeSlotNotification && selectedDate ? (
        <NotAvailableTimeSlotNotification
          date={
            selectedTimeSlot ??
            setHMToDate(
              selectedDate,
              selectedTimeOption?.data.hours ?? 0,
              selectedTimeOption?.data.minutes ?? 0,
            )
          }
          showTime={Boolean(selectedTimeSlot || selectedTimeOption)}
          partySize={partySize}
          areaName={selectedAreaName}
          regionalSettings={regionalSettings}
          textClassName={notificationClassName}
        />
      ) : null}

      {renderTagsList({
        layoutSize,
        label: t('uou-reservations.reservations.time-slots-text'),
        timeSlots: items,
        regionalSettings,
        onSelectTimeSlot,
        selectedTimeSlot,
      })}
    </div>
  )
}
